import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import db from '../firebaseConfig';
import '../styles.css';

function QuickCalculator() {
  const { country } = useParams();
  const isChina = country === 'china';
  const isKorea = country === 'korea';
  const isJapan = country === 'japan';
  const isThailand = country === 'thailand';
  const targetCurrencyName = '台幣';
  const targetCurrencyCode = 'TWD';
  
  // 對中國輸入為人民幣；對韓國輸入韓幣；泰國則輸入泰銖
  const [baseCost, setBaseCost] = useState('');
  const [weight, setWeight] = useState('');
  const [discount, setDiscount] = useState('60'); // 預設 60% (輸入60代表6折)

  // Rates
  const [exchangeRate, setExchangeRate] = useState(null);
  const [airRate, setAirRate] = useState(null);
  const [airTaiwanPerKg, setAirTaiwanPerKg] = useState(null);
  const [airIntlKrwPerKg, setAirIntlKrwPerKg] = useState(null);
  const [transitTaiwanPerKg, setTransitTaiwanPerKg] = useState(null);
  const [transitIntlKrwPerKg, setTransitIntlKrwPerKg] = useState(null);
  const [seaTaiwanPerKg, setSeaTaiwanPerKg] = useState(null);
  const [seaIntlKrwPerKg, setSeaIntlKrwPerKg] = useState(null);
  const [shippingRate, setShippingRate] = useState(null); // 中國用
  const [extraPercentage, setExtraPercentage] = useState(0); // 額外%數

  const [ratesError, setRatesError] = useState(null);

  useEffect(() => {
    async function fetchRates() {
      try {
        const ratesRef = doc(db, 'rates', country);
        const docSnap = await getDoc(ratesRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (isChina) {
            setExchangeRate(data.exchangeRate);
            setShippingRate(data.shippingRate);
            setExtraPercentage(data.extraPercentage || 0);
          } else if (isKorea) {
            setExchangeRate(data.exchangeRate);
            const airTw = data.airTaiwanPerKg ?? data.airRate;
            const trTw = data.transitTaiwanPerKg ?? data.transitRate;
            const seaTw = data.seaTaiwanPerKg ?? data.seaRate;
            setAirTaiwanPerKg(airTw !== undefined && airTw !== null ? airTw : 144);
            setAirIntlKrwPerKg(
              data.airIntlKrwPerKg !== undefined && data.airIntlKrwPerKg !== null
                ? data.airIntlKrwPerKg
                : 0
            );
            setTransitTaiwanPerKg(trTw !== undefined && trTw !== null ? trTw : 125);
            setTransitIntlKrwPerKg(
              data.transitIntlKrwPerKg !== undefined && data.transitIntlKrwPerKg !== null
                ? data.transitIntlKrwPerKg
                : 0
            );
            setSeaTaiwanPerKg(seaTw !== undefined && seaTw !== null ? seaTw : 95);
            setSeaIntlKrwPerKg(
              data.seaIntlKrwPerKg !== undefined && data.seaIntlKrwPerKg !== null
                ? data.seaIntlKrwPerKg
                : 0
            );
            setExtraPercentage(data.extraPercentage || 0);
          } else if (isJapan) {
            setExchangeRate(data.exchangeRate);
            setAirRate(data.airRate);
            setExtraPercentage(data.extraPercentage || 0);
          } else {
            setExchangeRate(data.exchangeRate);
            setAirRate(data.airRate);
            setExtraPercentage(data.extraPercentage || 0);
          }
        } else {
          setRatesError(`找不到 country "${country}" 的匯率設定`);
        }
      } catch (error) {
        setRatesError("讀取匯率時發生錯誤：" + error.message);
      }
    }
    if (country) fetchRates();
  }, [country]);

  if (ratesError) return <p>{ratesError}</p>;
  const ratesNotReady =
    exchangeRate === null ||
    (isChina && shippingRate === null) ||
    (isKorea &&
      (exchangeRate === null ||
        airTaiwanPerKg === null ||
        airIntlKrwPerKg === null ||
        transitTaiwanPerKg === null ||
        transitIntlKrwPerKg === null ||
        seaTaiwanPerKg === null ||
        seaIntlKrwPerKg === null)) ||
    ((isJapan || isThailand) && airRate === null);

  if (ratesNotReady) {
    return <p>載入中…</p>;
  }

  // 僅對於韓國顯示預設數字按鈕
  const presetValues = [];
  if (isKorea || isJapan) {
    for (let value = 1000; value <= 10000; value += 500) {
      presetValues.push(value);
    }
  }
  const handlePresetClick = (value) => {
    setBaseCost(value);
  };

  const parsedCost = parseFloat(baseCost) || 0;
  const parsedWeight = parseFloat(weight) || 0;
  const parsedDiscount = discount.trim() === '' ? 100 : parseFloat(discount) || 100;
  const hasDiscount = discount.trim() !== '' && parsedDiscount !== 100 && parsedDiscount !== 0;

  const baseCostLabel = isChina
    ? '人民幣成本：'
    : isKorea
    ? '韓幣成本：'
    : isJapan
    ? '日幣成本：'
    : '成本：';

  let convertedCost, discountedConvertedCost, convertedCostWithExtra, discountedConvertedCostWithExtra, finalTotals;
  if (isChina) {
    // 中國：成本以人民幣輸入
    convertedCost = Math.ceil(parsedCost * exchangeRate);
    // 原始成本的折扣後
    discountedConvertedCost = Math.ceil(parsedCost * exchangeRate * (parsedDiscount / 100));
    // 台幣換算+額外%數（原始成本先加%數，不打折）
    convertedCostWithExtra = Math.ceil(parsedCost * exchangeRate * (1 + extraPercentage / 100));
    // 折扣後成本再加額外%數：先打折，再加%數
    discountedConvertedCostWithExtra = Math.ceil(parsedCost * exchangeRate * (parsedDiscount / 100) * (1 + extraPercentage / 100));
    // 運費不打折，不加%數
    const shippingCost = Math.ceil(parsedWeight * shippingRate * exchangeRate);
    // 兩種總成本：原始成本折扣後+運費 和 折扣後成本加額外%數+運費
    const totalWithoutExtra = discountedConvertedCost + shippingCost;
    const totalWithExtra = discountedConvertedCostWithExtra + shippingCost;
    finalTotals = { 
      shippingCost, 
      convertedCostWithExtra, 
      discountedConvertedCost,
      discountedConvertedCostWithExtra,
      totalWithoutExtra,
      totalWithExtra
    };
  } else {
    // 韓國／泰國：成本換算方式為：成本除以匯率
    convertedCost = Math.ceil(parsedCost / exchangeRate);
    // 原始成本的折扣後
    discountedConvertedCost = Math.ceil(parsedCost / exchangeRate * (parsedDiscount / 100));
    // 台幣換算+額外%數（原始成本先加%數，不打折）
    convertedCostWithExtra = Math.ceil(parsedCost / exchangeRate * (1 + extraPercentage / 100));
    // 折扣後成本再加額外%數：先打折，再加%數
    discountedConvertedCostWithExtra = Math.ceil(parsedCost / exchangeRate * (parsedDiscount / 100) * (1 + extraPercentage / 100));
    
    if (isKorea) {
      const koreaLineShipping = (tw, krw) =>
        Math.ceil(parsedWeight * tw + (parsedWeight * krw) / exchangeRate);
      const airShipping = koreaLineShipping(airTaiwanPerKg, airIntlKrwPerKg);
      const transitShipping = koreaLineShipping(transitTaiwanPerKg, transitIntlKrwPerKg);
      const seaShipping = koreaLineShipping(seaTaiwanPerKg, seaIntlKrwPerKg);
      // 兩種總成本計算
      finalTotals = {
        // 原始成本折扣後的總成本
        airWithoutExtra: discountedConvertedCost + airShipping,
        seaWithoutExtra: discountedConvertedCost + seaShipping,
        transitWithoutExtra: discountedConvertedCost + transitShipping,
        // 折扣後成本加額外%數的總成本
        airWithExtra: discountedConvertedCostWithExtra + airShipping,
        seaWithExtra: discountedConvertedCostWithExtra + seaShipping,
        transitWithExtra: discountedConvertedCostWithExtra + transitShipping,
        airShipping,
        seaShipping,
        transitShipping,
        convertedCostWithExtra,
        discountedConvertedCost,
        discountedConvertedCostWithExtra
      };
    } else { // 日本與泰國皆僅有空運
      // 運費不打折，不加%數
      const airShipping = Math.ceil(parsedWeight * airRate);
      // 兩種總成本計算
      finalTotals = {
        // 原始成本折扣後的總成本
        airWithoutExtra: discountedConvertedCost + airShipping,
        // 折扣後成本加額外%數的總成本
        airWithExtra: discountedConvertedCostWithExtra + airShipping,
        airShipping,
        convertedCostWithExtra,
        discountedConvertedCost,
        discountedConvertedCostWithExtra
      };
    }
  }

  const pageStyle = {
    minHeight: '100vh',
    background: '#f8f9fa',
    padding: '1rem',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  };

  const cardStyle = {
    background: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    maxWidth: '480px',
    margin: '0 auto'
  };

  const formGroupStyle = {
    marginBottom: '1.25rem'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.9rem',
    fontWeight: '500',
    color: '#666',
    marginBottom: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    border: '1px solid #e5e5e5',
    borderRadius: '8px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s'
  };

  const resultCardStyle = {
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '0.75rem',
    fontSize: '0.95rem',
    lineHeight: '1.6'
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '1.5rem', color: '#333', textAlign: 'center' }}>快速計算</h2>

        <div style={formGroupStyle}>
          <label style={labelStyle}>{baseCostLabel}</label>
          <input
            type="number"
            value={baseCost}
            onChange={(e) => setBaseCost(e.target.value)}
            style={inputStyle}
          />
        </div>

        {(isKorea || isJapan) && (
          <div style={{ marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {presetValues.map((value) => (
              <button 
                key={value} 
                onClick={() => handlePresetClick(value)} 
                style={{
                  background: '#f5f5f5',
                  color: '#333',
                  border: '1px solid #e5e5e5',
                  borderRadius: '8px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => { e.target.style.background = '#eee'; }}
                onMouseOut={(e) => { e.target.style.background = '#f5f5f5'; }}
              >
                {value}
              </button>
            ))}
          </div>
        )}

        <div style={formGroupStyle}>
          <label style={labelStyle}>重量</label>
          <input
            type="number"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            style={inputStyle}
          />
        </div>

        <div style={formGroupStyle}>
          <label style={labelStyle}>折扣 (輸入60代表6折)</label>
          <input
            type="number"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            placeholder="例如：60 (代表6折)"
            style={inputStyle}
          />
        </div>

        <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e5e5e5' }}>

          {country === 'china' ? (
            <div>
              <div style={{ ...resultCardStyle, background: '#f8f9fa', border: '1px solid #e5e5e5' }}>
                <strong>原始換算成本：</strong> {convertedCost} {targetCurrencyCode}
              </div>
              {extraPercentage > 0 && (
                <div style={{ ...resultCardStyle, background: '#f0f9f4', border: '1px solid #d4edda' }}>
                  <strong>台幣換算+額外{extraPercentage}%：</strong> {finalTotals.convertedCostWithExtra} {targetCurrencyCode}
                </div>
              )}
              {hasDiscount && (
                <div style={{ ...resultCardStyle, background: '#fffbf0', border: '1px solid #ffeaa7' }}>
                  <strong>{parsedDiscount}%折扣後成本（原始）：</strong> {finalTotals.discountedConvertedCost} {targetCurrencyCode}
                </div>
              )}
              {hasDiscount && extraPercentage > 0 && (
                <div style={{ ...resultCardStyle, background: '#fffbf0', border: '1px solid #ffeaa7' }}>
                  <strong>{parsedDiscount}%折扣後+{extraPercentage}%成本：</strong> {finalTotals.discountedConvertedCostWithExtra} {targetCurrencyCode}
                </div>
              )}
              <div style={{ ...resultCardStyle, background: '#fff5f5', border: '1px solid #ffcccc' }}>
                <strong>🚢 運費：</strong> {finalTotals.shippingCost} {targetCurrencyCode}
              </div>
              <div style={{ ...resultCardStyle, background: '#e8f4fd', border: '1px solid #b3d9ff' }}>
                <strong>🚢 總成本（{hasDiscount ? `${parsedDiscount}%折扣後` : '原始成本'}{extraPercentage > 0 ? `+額外${extraPercentage}%` : ''}+運費）：</strong> {extraPercentage > 0 ? finalTotals.totalWithExtra : finalTotals.totalWithoutExtra} {targetCurrencyCode}
              </div>
            </div>
          ) : (
            <div>
              <div style={{ ...resultCardStyle, background: '#f8f9fa', border: '1px solid #e5e5e5' }}>
                <strong>原始換算成本：</strong> {convertedCost} {targetCurrencyCode}
              </div>
              {extraPercentage > 0 && (
                <div style={{ ...resultCardStyle, background: '#f0f9f4', border: '1px solid #d4edda' }}>
                  <strong>{targetCurrencyName}換算+額外{extraPercentage}%：</strong> {finalTotals.convertedCostWithExtra} {targetCurrencyCode}
                </div>
              )}
              {hasDiscount && (
                <div style={{ ...resultCardStyle, background: '#fffbf0', border: '1px solid #ffeaa7' }}>
                  <strong>💰 {parsedDiscount}%折扣後成本（原始）：</strong> {finalTotals.discountedConvertedCost} {targetCurrencyCode}
                </div>
              )}
              {hasDiscount && extraPercentage > 0 && (
                <div style={{ ...resultCardStyle, background: '#fffbf0', border: '1px solid #ffeaa7' }}>
                  <strong>💰 {parsedDiscount}%折扣後+{extraPercentage}%成本：</strong> {finalTotals.discountedConvertedCostWithExtra} {targetCurrencyCode}
                </div>
              )}
              {isKorea && (
                <>
                  <div style={{ ...resultCardStyle, background: '#fff5f5', border: '1px solid #ffcccc' }}>
                    <strong>✈️ 轉機空運運費：</strong> {finalTotals.transitShipping} {targetCurrencyCode}
                  </div>
                  <div style={{ ...resultCardStyle, background: '#fff5f5', border: '1px solid #ffcccc' }}>
                    <strong>🚀 直飛空運運費：</strong> {finalTotals.airShipping} {targetCurrencyCode}
                  </div>
                  <div style={{ ...resultCardStyle, background: '#fff5f5', border: '1px solid #ffcccc' }}>
                    <strong>🚢 海快運費：</strong> {finalTotals.seaShipping} {targetCurrencyCode}
                  </div>
                  <div style={{ ...resultCardStyle, background: '#e8f4fd', border: '1px solid #b3d9ff' }}>
                    <strong>✈️ 轉機空運總成本（{hasDiscount ? `${parsedDiscount}%折扣後` : '原始成本'}{extraPercentage > 0 ? `+額外${extraPercentage}%` : ''}+運費）：</strong> {extraPercentage > 0 ? finalTotals.transitWithExtra : finalTotals.transitWithoutExtra} {targetCurrencyCode}
                  </div>
                  <div style={{ ...resultCardStyle, background: '#e8f4fd', border: '1px solid #b3d9ff' }}>
                    <strong>🚀 直飛空運總成本（{hasDiscount ? `${parsedDiscount}%折扣後` : '原始成本'}{extraPercentage > 0 ? `+額外${extraPercentage}%` : ''}+運費）：</strong> {extraPercentage > 0 ? finalTotals.airWithExtra : finalTotals.airWithoutExtra} {targetCurrencyCode}
                  </div>
                  <div style={{ ...resultCardStyle, background: '#e8f4fd', border: '1px solid #b3d9ff' }}>
                    <strong>🚢 海快總成本（{hasDiscount ? `${parsedDiscount}%折扣後` : '原始成本'}{extraPercentage > 0 ? `+額外${extraPercentage}%` : ''}+運費）：</strong> {extraPercentage > 0 ? finalTotals.seaWithExtra : finalTotals.seaWithoutExtra} {targetCurrencyCode}
                  </div>
                </>
              )}
              {(isThailand || isJapan) && (
                <>
                  <div style={{ ...resultCardStyle, background: '#fff5f5', border: '1px solid #ffcccc' }}>
                    <strong>🚀 空運運費：</strong> {finalTotals.airShipping} {targetCurrencyCode}
                  </div>
                  <div style={{ ...resultCardStyle, background: '#e8f4fd', border: '1px solid #b3d9ff' }}>
                    <strong>🚀 空運總成本（{hasDiscount ? `${parsedDiscount}%折扣後` : '原始成本'}{extraPercentage > 0 ? `+額外${extraPercentage}%` : ''}+運費）：</strong> {extraPercentage > 0 ? finalTotals.airWithExtra : finalTotals.airWithoutExtra} {targetCurrencyCode}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default QuickCalculator;
